import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Divider, IconButton, Menu, MenuItem, Popover, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  DndContext, DragOverlay, PointerSensor, closestCenter, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UNIT_OPTIONS } from './offerteUi';
import { basisHerkomst, euro, formatSyncTijd, korteBasisNaam, PRODUCT_VELDEN_LEEG } from './basisofferte';
import {
  groepeer, hernoemSectie, historieTekst, metSleutels, nieuweSectieNaam, productOpties,
  regelTotaal, regelUitOptie, schuifRegel, schuifSectie, totalen, verplaatsRegel, verplaatsSectie,
  verwijderRegel, verwijderSectie, voegRegelToe, voegSectieToe, zetSectieAantal,
} from './regelBewerking';

const GRIJS = '#94a3b8';
const RAND = '#eef2f6';
const ORANJE = '#b45309';
// Laatste kolom: prullenbak + ⋯ naast elkaar (wiki Offerte generator, 30 okt: snel regels weghalen).
const KOLOMMEN = '28px minmax(0,1fr) 64px 96px 92px 112px 92px 64px';

const eenheidWaarde = (u) => (UNIT_OPTIONS.includes(u) ? u
  : /min/i.test(u || '') ? 'minuut film'
    : /uur|uren|hour/i.test(u || '') ? 'uur'
      : /dag|day/i.test(u || '') ? 'dag'
        : /km/i.test(u || '') ? 'km'
          : /prijs|price/i.test(u || '') ? 'prijs' : 'stuk');

const sectieId = (naam) => `s:${naam}`;
const kopId = (naam) => `h:${naam}`;
const regelId = (k) => `l:${k}`;

// De greep ⠿: slepen met muis/touchpad, of focussen en ↑/↓ voor één plek.
function Greep({ label, onSchuif, listeners, attributes, setActivatorNodeRef, id }) {
  return (
    <Box ref={setActivatorNodeRef} {...attributes} {...listeners} tabIndex={0} role="button" data-greep={id}
      aria-label={`${label} — sleep, of gebruik pijl omhoog/omlaag`}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault(); e.stopPropagation();
          onSchuif(e.key === 'ArrowUp' ? -1 : 1);
        }
      }}
      sx={{ display: 'grid', placeItems: 'center', cursor: 'grab', color: '#cbd5e1', borderRadius: 1, width: 24, height: 28,
        touchAction: 'none', '&:hover': { color: GRIJS }, '&:focus-visible': { outline: '2px solid #a5b4fc', color: '#4338ca' } }}>
      <DragIndicatorIcon sx={{ fontSize: 18 }} />
    </Box>
  );
}

function RegelRij({ regel, pc, opties, onWijzig, onSchuif, onMeer, onVerwijder, gesleept, language }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: regelId(regel._k), data: { soort: 'regel', sleutel: regel._k, sectie: regel.section || '' },
  });
  const herkomst = basisHerkomst(regel, pc?.basis);
  const hist = historieTekst(pc);
  const gekozen = useMemo(() => opties.find((o) => o.productId != null && o.productId === regel.productId) || null,
    [opties, regel.productId]);
  const code = regel.code || gekozen?.code;
  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={{ display: 'grid', gridTemplateColumns: KOLOMMEN, columnGap: 1, alignItems: 'start', py: 0.75,
        borderBottom: `1px solid ${RAND}`, bgcolor: regel.needsReview ? '#fffdf5' : '#fff',
        opacity: isDragging || gesleept ? 0.35 : 1 }}>
      <Greep id={regelId(regel._k)} label={regel.description || 'Regel'} onSchuif={onSchuif} listeners={listeners}
        attributes={attributes} setActivatorNodeRef={setActivatorNodeRef} />
      <Box sx={{ minWidth: 0 }}>
        <Autocomplete freeSolo size="small" options={opties} value={regel.description || ''}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
          filterOptions={(lijst, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            const uit = q ? lijst.filter((o) => `${o.label} ${o.code || ''}`.toLowerCase().includes(q)) : lijst;
            return uit.slice(0, 60);
          }}
          isOptionEqualToValue={(o, v) => (typeof v === 'string' ? o.label === v : o === v)}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={`${o.soort}-${o.productId}-${o.label}`}
              sx={{ display: 'flex', gap: 1, alignItems: 'baseline', fontSize: 14 }}>
              <span style={{ flex: 1 }}>{o.label}{o.code && <span style={{ color: GRIJS }}> · {o.code}</span>}</span>
              {o.soort === 'basis' && o.prijs != null && (
                <span style={{ color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>{euro(o.prijs)}/{o.eenheid}</span>
              )}
            </Box>
          )}
          onChange={(_e, val) => onWijzig(typeof val === 'object' && val ? regelUitOptie(regel, val)
            : { ...regel, ...PRODUCT_VELDEN_LEEG, description: val || '', product: val || '', productId: null })}
          onInputChange={(_e, val, reason) => {
            if (reason === 'input') onWijzig({ ...regel, ...PRODUCT_VELDEN_LEEG, description: val, product: val, productId: null });
          }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" fullWidth placeholder="Product of omschrijving"
              InputProps={{ ...params.InputProps, disableUnderline: true, sx: { fontSize: 14.5, fontWeight: 600 } }} />
          )} />
        {(code || herkomst || regel.detail || regel.hidden || regel.titel) && (
          <Typography variant="caption" component="div" sx={{ color: GRIJS, lineHeight: 1.35, mt: 0.25 }}>
            {regel.hidden && <VisibilityOffOutlinedIcon titleAccess="Verborgen bij urenschrijven" sx={{ fontSize: 13, mr: 0.5, verticalAlign: '-2px' }} />}
            {regel.titel ? `${regel.titel} · ` : ''}
            {/* Herkomst staat éénmaal boven de tabel; per regel alleen de code, met de herkomst als tooltip. */}
            {code && <span title={herkomst ? herkomst.tekst : undefined}>{code}</span>}
            {herkomst?.override && <span title={herkomst.override.reden} style={{ color: ORANJE }}>{code ? ' · ' : ''}override</span>}
            {herkomst?.afwijking && (
              <Box component="span" role="button" tabIndex={0}
                onClick={() => onWijzig({ ...regel, sellingprice: herkomst.afwijking.gripp })}
                onKeyDown={(e) => { if (e.key === 'Enter') onWijzig({ ...regel, sellingprice: herkomst.afwijking.gripp }); }}
                sx={{ color: ORANJE, cursor: 'pointer', textDecoration: 'underline dotted' }}>
                {' '}· Gripp {euro(herkomst.afwijking.gripp)}
              </Box>
            )}
            {regel.detail && <Box component="span" sx={{ display: 'block', fontStyle: 'italic', color: '#64748b' }}>{regel.detail}</Box>}
          </Typography>
        )}
        {/* Engelse offerte zonder Engels Gripp-product: zichtbaar i.p.v. stil een NL-regeltitel in de PDF. */}
        {language === 'en' && regel.englishGap && !regel.productId && (
          <Typography variant="caption" sx={{ color: ORANJE, display: 'block' }}>
            Geen Engels Gripp-product — titel wordt het NL-product + deze tekst
          </Typography>
        )}
      </Box>
      <TextField variant="standard" type="number" value={regel.amount ?? 1}
        onChange={(e) => onWijzig({ ...regel, amount: e.target.value })}
        inputProps={{ 'aria-label': 'Aantal', style: { textAlign: 'right' } }} InputProps={{ disableUnderline: true }} />
      <TextField variant="standard" select value={eenheidWaarde(regel.unit)} InputProps={{ disableUnderline: true }}
        onChange={(e) => onWijzig({ ...regel, unit: e.target.value, unitId: null })}
        SelectProps={{ 'aria-label': 'Eenheid' }} sx={{ '& .MuiSelect-select': { fontSize: 14, color: '#475569' } }}>
        {UNIT_OPTIONS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
      </TextField>
      <TextField variant="standard" type="number" value={regel.sellingprice ?? 0}
        onChange={(e) => onWijzig({ ...regel, sellingprice: e.target.value })}
        inputProps={{ 'aria-label': 'Stukprijs', style: { textAlign: 'right' } }} InputProps={{ disableUnderline: true }} />
      <Box sx={{ pt: 0.6, minWidth: 0 }}>
        {hist ? (
          <Tooltip title={pc?.min != null ? `Historie: €${pc.min}–€${pc.max} (mediaan €${pc.median}, ${pc.count} regels)` : ''}>
            <Typography variant="caption" sx={{ color: GRIJS, whiteSpace: 'nowrap' }}>
              {hist.tekst}
              {hist.afwijking && <span style={{ color: hist.ernst === 'warn' ? '#dc2626' : ORANJE, fontWeight: 700 }}> {hist.afwijking}</span>}
            </Typography>
          </Tooltip>
        ) : <Typography variant="caption" sx={{ color: '#e2e8f0' }}>—</Typography>}
      </Box>
      <Typography variant="body2" align="right" sx={{ pt: 0.6, color: '#334155', whiteSpace: 'nowrap' }}>
        {euro(regelTotaal(regel))}
      </Typography>
      {/* Verwijderen in één klik; de container biedt ongedaan maken (toast + Ctrl/Cmd+Z). Rustig grijs,
          rood pas bij hover/focus — anders staat er een kolom rode prullenbakken in beeld. */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        <Tooltip title="Regel verwijderen">
          <IconButton size="small" aria-label={`Regel verwijderen: ${regel.description || 'zonder omschrijving'}`}
            onClick={() => onVerwijder(regel)}
            sx={{ color: '#cbd5e1', '&:hover, &:focus-visible': { color: '#dc2626' } }}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" aria-label="Meer" onClick={(e) => onMeer(e.currentTarget, regel)} sx={{ color: GRIJS }}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function SectieBlok({ groep, subtotaal, open, onToggle, kinderen, onHernoem, onAantal, onSchuif, onRegel, onVerwijder, gesleept }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: sectieId(groep.naam), data: { soort: 'sectie', sectie: groep.naam },
    // Regels zonder sectie staan vast bovenaan (geen groepskop in Gripp).
    disabled: groep.naam === '',
  });
  // De kop is ook een landingsplek voor een regel (bovenaan in deze sectie).
  const { setNodeRef: kopRef, isOver } = useDroppable({ id: kopId(groep.naam), data: { soort: 'kop', sectie: groep.naam } });
  const [naam, setNaam] = useState(groep.naam);
  useEffect(() => { setNaam(groep.naam); }, [groep.naam]);
  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={{ mb: 1.5, opacity: isDragging || gesleept ? 0.35 : 1 }}>
      <Stack ref={kopRef} direction="row" alignItems="center" spacing={0.5}
        sx={{ py: 0.5, borderBottom: '1px solid #e2e8f0', bgcolor: isOver ? '#eef2ff' : 'transparent', borderRadius: 1 }}>
        {groep.naam === '' ? <Box sx={{ width: 24, flexShrink: 0 }} /> : (
          <Greep id={sectieId(groep.naam)} label={`Sectie ${groep.naam}`} onSchuif={onSchuif} listeners={listeners}
            attributes={attributes} setActivatorNodeRef={setActivatorNodeRef} />
        )}
        <IconButton size="small" onClick={onToggle} aria-label={open ? 'Inklappen' : 'Uitklappen'} sx={{ color: GRIJS, p: 0.25 }}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <TextField variant="standard" value={naam} placeholder="Sectienaam"
          onChange={(e) => setNaam(e.target.value)}
          onBlur={() => { if (naam !== groep.naam) onHernoem(naam); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          InputProps={{ disableUnderline: true, sx: { fontSize: 15, fontWeight: 800, color: '#1f2937' } }}
          inputProps={{ 'aria-label': 'Sectienaam' }} sx={{ flex: 1, minWidth: 0 }} />
        <Tooltip title="Aantal op het groepskopje in de PDF (bv. 2 draaidagen) — telt niet mee in het totaal">
          <TextField variant="standard" type="number" value={groep.aantal ?? ''} placeholder="1"
            onChange={(e) => onAantal(e.target.value)}
            InputProps={{ disableUnderline: true, startAdornment: <Typography variant="caption" sx={{ color: GRIJS, mr: 0.25 }}>×</Typography> }}
            inputProps={{ 'aria-label': 'Aantal op het groepskopje', style: { width: 28, textAlign: 'right', fontSize: 13 } }} />
        </Tooltip>
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700, minWidth: 92, textAlign: 'right' }}>
          {euro(subtotaal)}
        </Typography>
        <SectieMenu onVerwijder={onVerwijder} />
      </Stack>
      {open && (
        <>
          {kinderen}
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={onRegel}
            sx={{ textTransform: 'none', color: '#64748b', mt: 0.25, ml: 3.5 }}>
            Regel
          </Button>
        </>
      )}
      {!open && (
        <Typography variant="caption" sx={{ color: GRIJS, ml: 7 }}>
          {groep.regels.length} regel{groep.regels.length === 1 ? '' : 's'}
        </Typography>
      )}
    </Box>
  );
}

function SectieMenu({ onVerwijder }) {
  const [anker, setAnker] = useState(null);
  return (
    <>
      <IconButton size="small" aria-label="Sectie-opties" onClick={(e) => setAnker(e.currentTarget)} sx={{ color: '#cbd5e1' }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anker} open={!!anker} onClose={() => setAnker(null)}>
        <MenuItem onClick={() => { setAnker(null); onVerwijder(); }}>Sectie met regels verwijderen</MenuItem>
      </Menu>
    </>
  );
}

// ⋯ per regel: inkoop, verborgen bij urenschrijven, titel en toelichting — zoals in Gripp.
function RegelDetails({ anker, regel, onSluit, onWijzig, onVerwijder }) {
  if (!regel) return null;
  return (
    <Popover open={!!anker} anchorEl={anker} onClose={onSluit}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
        <TextField size="small" label="Inkoop per eenheid (€)" type="number" value={regel.buyingprice ?? ''}
          onChange={(e) => onWijzig({ ...regel, buyingprice: e.target.value })} />
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2">Verborgen bij urenschrijven</Typography>
          <Switch size="small" checked={!!regel.hidden} onChange={(e) => onWijzig({ ...regel, hidden: e.target.checked })} />
        </Stack>
        <TextField size="small" label="Titel (bv. v1, Thumbnail)" value={regel.titel || ''}
          onChange={(e) => onWijzig({ ...regel, titel: e.target.value })} />
        <TextField size="small" label="Toelichting op de offerte" multiline minRows={2} value={regel.detail || ''}
          onChange={(e) => onWijzig({ ...regel, detail: e.target.value })} />
        <Divider />
        <Button size="small" color="error" onClick={onVerwijder} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
          Regel verwijderen
        </Button>
      </Stack>
    </Popover>
  );
}

/**
 * Offerteregels als secties (= de groepen in Gripp), met slepen binnen en tussen
 * secties, hele secties slepen, product-combobox op de Gripp-catalogus en de
 * basisoffertes, en totalen. `setLines(nieuw, label)` legt elke wijziging vast; bij
 * herordenen en verwijderen biedt de container ongedaan maken aan.
 */
export default function OfferteRegels({ lines, setLines, priceCheck, products, catalogus, onLoadCatalogus, language = 'nl', standaardBtw = null }) {
  const [dicht, setDicht] = useState({});
  const [actief, setActief] = useState(null);
  const [meer, setMeer] = useState({ anker: null, sleutel: null });
  // Na ↑/↓ over een sectiegrens wordt de rij opnieuw gemount: daarna de focus terug op zijn greep.
  const [focusNa, setFocusNa] = useState(null);
  useEffect(() => {
    if (!focusNa) return;
    const esc = window.CSS && window.CSS.escape ? window.CSS.escape(focusNa) : focusNa.replace(/"/g, '\\"');
    const el = document.querySelector(`[data-greep="${esc}"]`);
    if (el) el.focus();
    setFocusNa(null);
  }, [focusNa, lines]);
  const schuifMetToetsen = (k, richting) => {
    const nieuw = schuifRegel(lines, k, richting);
    if (nieuw === lines) return;
    const doel = nieuw.find((l) => l._k === k);
    if (doel) setDicht((d) => ({ ...d, [String(doel.section ?? '').trim()]: false }));
    setLines(nieuw, 'Regel verplaatst');
    setFocusNa(`l:${k}`);
  };
  const schuifSectieMetToetsen = (naam, richting) => {
    const nieuw = schuifSectie(lines, naam, richting);
    if (nieuw === lines) return;
    setLines(nieuw, 'Sectie verplaatst');
    setFocusNa(`s:${naam}`);
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Elke regel een stabiele sleutel — nodig voor slepen en voor de juiste React-keys.
  useEffect(() => {
    const metK = metSleutels(lines);
    if (metK !== lines) setLines(metK, null);
  }, [lines, setLines]);
  useEffect(() => { if (onLoadCatalogus) onLoadCatalogus(); }, [onLoadCatalogus]);

  const groepen = useMemo(() => groepeer(lines), [lines]);
  const tot = useMemo(() => totalen(lines, standaardBtw), [lines, standaardBtw]);
  const opties = useMemo(() => productOpties(catalogus, products), [catalogus, products]);
  const pcOpSleutel = useMemo(() => {
    const m = {};
    (lines || []).forEach((l, i) => { if (l?._k) m[l._k] = (priceCheck || [])[i]; });
    return m;
  }, [lines, priceCheck]);
  const regelOp = (k) => (lines || []).find((l) => l._k === k);
  const wijzig = (nieuw) => setLines((lines || []).map((l) => (l._k === nieuw._k ? nieuw : l)), null);
  // Eén pad voor de prullenbak in de rij en de knop in het ⋯-paneel: met label, dus met ongedaan maken.
  const verwijder = (k) => {
    if (meer.sleutel === k) setMeer({ anker: null, sleutel: null });
    // Toetsenbord: de focus gaat naar de greep van de buurregel, anders ben je je plek kwijt.
    const lijst = (lines || []).filter((l) => l._k);
    const i = lijst.findIndex((l) => l._k === k);
    const buur = lijst[i + 1] || lijst[i - 1];
    setLines(verwijderRegel(lines, k), 'Regel verwijderd');
    if (buur) setFocusNa(`l:${buur._k}`);
  };

  const botsing = (args) => {
    const soort = args.active.data.current?.soort;
    const doelen = args.droppableContainers.filter((c) => {
      const s = c.data.current?.soort;
      return soort === 'sectie' ? s === 'sectie' : (s === 'regel' || s === 'kop');
    });
    return closestCenter({ ...args, droppableContainers: doelen });
  };

  // Alleen echt veranderde regels doorgeven: een geweigerde verplaatsing geeft geen toast.
  const zet = (nieuw, label) => { if (nieuw !== lines) setLines(nieuw, label); };
  const naSlepen = ({ active, over }) => {
    setActief(null);
    if (!over || active.id === over.id) return;
    const a = active.data.current || {};
    const o = over.data.current || {};
    if (a.soort === 'sectie') {
      const naar = groepen.findIndex((g) => g.naam === o.sectie);
      if (naar >= 0) zet(verplaatsSectie(lines, a.sectie, naar), 'Sectie verplaatst');
      return;
    }
    if (o.soort === 'kop') {
      zet(verplaatsRegel(lines, a.sleutel, o.sectie, 0), 'Regel verplaatst');
      return;
    }
    if (o.soort === 'regel') {
      const doel = groepen.find((g) => g.naam === o.sectie);
      const naarIndex = doel ? doel.regels.findIndex((l) => l._k === o.sleutel) : 0;
      const zelfde = a.sectie === o.sectie;
      const vanIndex = zelfde ? doel.regels.findIndex((l) => l._k === a.sleutel) : -1;
      // Binnen de sectie naar beneden: op de plek ná de doelregel (zoals arrayMove).
      const index = zelfde && vanIndex < naarIndex ? naarIndex : naarIndex;
      zet(verplaatsRegel(lines, a.sleutel, o.sectie, index), 'Regel verplaatst');
    }
  };

  const actieveRegel = actief?.soort === 'regel' ? regelOp(actief.sleutel) : null;
  const meerRegel = meer.sleutel ? regelOp(meer.sleutel) : null;

  // Uit welke basisofferte(s) de regels komen en wanneer gesynct — één keer, niet per regel.
  const bronnen = [...new Map((lines || []).filter((l) => l?.basis?.naam)
    .map((l) => [l.basis.naam, l.basis])).values()];
  const laatsteSync = bronnen.map((b) => b.gesynct).filter(Boolean).sort().pop();

  return (
    <Box sx={{ overflowX: 'auto' }}>
    <Box sx={{ minWidth: 860 }}>
      {bronnen.length > 0 && (
        <Typography variant="caption" component="div" sx={{ color: GRIJS, mb: 1 }}>
          Producten en prijzen uit basisofferte {bronnen.map((b) => `“${korteBasisNaam(b.naam)}”`).join(', ')}
          {laatsteSync ? ` · gesynct ${formatSyncTijd(laatsteSync)}` : ''}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: KOLOMMEN, columnGap: 1, px: 0, pb: 0.5 }}>
        {['', 'Product / omschrijving', 'Aantal', 'Eenheid', 'Stukprijs', 'Historie', 'Totaal', ''].map((k, i) => (
          <Typography key={i} variant="caption" align={[2, 4, 6].includes(i) ? 'right' : 'left'}
            sx={{ color: GRIJS, fontWeight: 700, letterSpacing: '0.04em' }}>{k}</Typography>
        ))}
      </Box>
      <DndContext sensors={sensors} collisionDetection={botsing}
        onDragStart={({ active }) => setActief(active.data.current || null)}
        onDragCancel={() => setActief(null)} onDragEnd={naSlepen}>
        <SortableContext items={groepen.map((g) => sectieId(g.naam))} strategy={verticalListSortingStrategy}>
          {groepen.map((g, gi) => (
            <SectieBlok key={sectieId(g.naam)} groep={g} subtotaal={tot.perSectie[g.naam] || 0}
              open={!dicht[g.naam] && actief?.soort !== 'sectie'} gesleept={false}
              onToggle={() => setDicht((d) => ({ ...d, [g.naam]: !d[g.naam] }))}
              onHernoem={(nieuw) => setLines(hernoemSectie(lines, g.naam, nieuw), null)}
              onAantal={(n) => setLines(zetSectieAantal(lines, g.naam, n), null)}
              onSchuif={(r) => schuifSectieMetToetsen(g.naam, r)}
              onRegel={() => setLines(voegRegelToe(lines, g.naam), null)}
              onVerwijder={() => setLines(verwijderSectie(lines, g.naam), `Sectie “${g.naam || 'zonder naam'}” verwijderd`)}
              kinderen={(
                <SortableContext items={g.regels.map((l) => regelId(l._k || `${gi}`))} strategy={verticalListSortingStrategy}>
                  {g.regels.filter((l) => l._k).map((l) => (
                    <RegelRij key={l._k} regel={l} pc={pcOpSleutel[l._k]} opties={opties}
                      gesleept={false} onWijzig={wijzig} language={language}
                      onSchuif={(r) => schuifMetToetsen(l._k, r)}
                      onMeer={(anker, regel) => setMeer({ anker, sleutel: regel._k })}
                      onVerwijder={(regel) => verwijder(regel._k)} />
                  ))}
                </SortableContext>
              )} />
          ))}
        </SortableContext>
        <DragOverlay>
          {actieveRegel && (
            <Box sx={{ px: 1.5, py: 1, bgcolor: '#fff', borderRadius: 1.5, boxShadow: 3, fontWeight: 600, fontSize: 14 }}>
              {actieveRegel.description || 'Regel'} · {euro(regelTotaal(actieveRegel))}
            </Box>
          )}
          {actief?.soort === 'sectie' && (
            <Box sx={{ px: 1.5, py: 1, bgcolor: '#fff', borderRadius: 1.5, boxShadow: 3, fontWeight: 800, fontSize: 15 }}>
              {actief.sectie || 'Sectie'}
            </Box>
          )}
        </DragOverlay>
      </DndContext>

      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={() => setLines(voegSectieToe(lines, nieuweSectieNaam(lines)), null)}
        sx={{ textTransform: 'none', color: '#64748b' }}>
        Sectie
      </Button>

      <RegelDetails anker={meer.anker} regel={meerRegel} onSluit={() => setMeer({ anker: null, sleutel: null })}
        onWijzig={wijzig}
        onVerwijder={() => verwijder(meer.sleutel)} />

      {/* Totalen — zoals het Totalen-paneel in Gripp, rechts uitgelijnd. */}
      <Box sx={{ ml: 'auto', mt: 1.5, width: 300, maxWidth: '100%' }}>
        {[
          ['Totaal excl. btw', tot.excl, true],
          [`Btw ${tot.btwPct}%`, tot.btw],
          ['Totaal incl. btw', tot.incl],
        ].map(([k, v, vet]) => (
          <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
            <Typography variant="body2" sx={{ color: vet ? '#111827' : '#64748b', fontWeight: vet ? 800 : 400 }}>{k}</Typography>
            <Typography variant="body2" sx={{ color: vet ? '#111827' : '#64748b', fontWeight: vet ? 800 : 400 }}>{euro(v)}</Typography>
          </Stack>
        ))}
        {tot.marge != null && (
          <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25, mt: 0.5, borderTop: `1px solid ${RAND}` }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Marge (na inkoop {euro(tot.inkoop)})</Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>{euro(tot.marge)}{tot.margePct != null ? ` · ${tot.margePct}%` : ''}</Typography>
          </Stack>
        )}
      </Box>
    </Box>
    </Box>
  );
}
